// components/NewUi/TreatmentFlow/PatientDetails.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useMutation } from "@tanstack/react-query";

import flow from "./flow.module.scss";
import { toast } from "react-toastify";
import styles from "./patientDetails.module.scss";
import Lottie from "lottie-react";
import generatingAnim from "./animations/Female Employee Working on Data Security.json";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";




export default function PatientDetails({ onNext, onBack, treatmentData, setTreatmentData }) {
  const [clinician, setClinician] = useState("");
  const [applyTemplate, setApplyTemplate] = useState(true);
  const [note, setNote] = useState("");
  const [dueOption, setDueOption] = useState("2 weeks");
  const [customDate, setCustomDate] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [hasEditedNote, setHasEditedNote] = useState(false);
  const [showEmptyNoteModal, setShowEmptyNoteModal] = useState(false);
  const [showExampleModal, setShowExampleModal] = useState(false);
  const allTreatments = treatmentData?.treatment_plan?.treatments || [];
  const totalPrice = allTreatments.reduce((sum, t) => sum + (t.price || 0), 0);


  const consentBundleId = treatmentData?.id;

  useEffect(() => {
    if (treatmentData?.clinician) {
      setClinician(treatmentData.clinician.id.toString());
    }
  }, [treatmentData]);

useEffect(() => {
  if (treatmentData?.notes && !hasEditedNote) {
    setNote(treatmentData.notes);
  }
}, [treatmentData]);

  const patient = treatmentData?.patient || {};
  const clinicianObj = treatmentData?.clinician || {};
  const treatmentPlan = treatmentData?.treatment_plan?.treatments?.[0];

  const reloadTreatmentData = async () => {
    try {
      const res = await axios.get(`consent_bundle/${consentBundleId}`);
      setTreatmentData(res.data);
      console.log("Consent bundle reloaded");
    } catch (err) {
      console.error("Error reloading consent bundle:", err);
    }
  };

  /* ---------------------- Generate Patient Letter with toast ---------------------- */
 const generatePatientLetter = useMutation(
  async () => {
    if (!note || note.trim() === "") {
      toast.info("No patient note provided — skipping letter generation.");
      onNext();
      return null;
    } else {
      setIsGenerating(true);
      await new Promise((resolve) => setTimeout(resolve, 500));

      let attempt = 0;
      const maxAttempts = 3;
      let lastError;

      while (attempt < maxAttempts) {
        try {
          const base = process.env.REACT_APP_BASE_URL_V2;
          if (!base) throw new Error("REACT_APP_BASE_URL_V2 is not set");

          const endpoint = `${process.env.REACT_APP_BASE_URL_V2}consent_bundle/${consentBundleId}/generate_patient_letter`;

          const response = await axios.put(endpoint, {
            patient_notes: note || "",
          });

          return response; 
        } catch (err) {
          attempt++;
          lastError = err;
          console.warn(`Attempt ${attempt} failed, retrying...`);
          toast.error("Failed to generate, retrying...");
          if (attempt < maxAttempts) {
            await new Promise((r) => setTimeout(r, 1000));
          }
        }
      }

      throw lastError;
    }
  },
  {
    onSuccess: async (response) => {
      if (!response) return;

      toast.success("Patient consent letter generated successfully!");
      console.log("Patient letter generated");
      setIsGenerating(false);

      try {
        await reloadTreatmentData();
        console.log("Treatment data refreshed after letter generation");
      } catch (err) {
        console.error("Error refreshing data after letter generation:", err);
      }

      onNext();
    },
    onError: (error) => {
      console.error("All retries failed:", error);
      toast.error("Failed to generate the letter after 3 attempts. Proceeding to next step.");
      setIsGenerating(false);
      onNext();
    },
  }
);



  const setPractitioner = useMutation(
  ({ practitioner_id, load_clinician_templates }) =>
    axios.put(
      `/consent_bundle/${consentBundleId}/set_practitioner_for_consent_bundle`,
      { practitioner_id, load_clinician_templates }
    ),
  {
    onSuccess: (_data, variables) => {
      console.log("Practitioner updated successfully");

      // ✅ Only generate if user actually edited the note
      if (variables?.should_generate_letter) {
        generatePatientLetter.mutate();
      } else {
        onNext();
      }
    },
    onError: (error) => {
      console.error("Error setting practitioner:", error);
      toast.error("Failed to set clinician. Please try again.");
    },
  }
);


  const updateAppointment = useMutation(
    (payload) => {
      const base = process.env.REACT_APP_BASE_URL_V2;
      if (!base) throw new Error("REACT_APP_BASE_URL_V2 is not set");

      const endpoint = `${process.env.REACT_APP_BASE_URL_V2}consent_bundle/${consentBundleId}/update_appointment_date`;

      return axios.put(endpoint, payload);
    },
    {
      onSuccess: () => {
        console.log("Appointment date updated successfully");
        reloadTreatmentData();
      },
      onError: (err) => {
        console.error("Error updating appointment date:", err);
        toast.error("Failed to update appointment date. Please try again.");
      },
    }
  );


  const handleDueOptionChange = (value) => {
    setDueOption(value);
    setCustomDate("");
    if (value) {
      updateAppointment.mutate({ auto_appointment_date_in: value });
    }
  };

  const handleCustomDateChange = (value) => {
    setCustomDate(value);
    setDueOption("");
    if (value) {
      updateAppointment.mutate({ appointment_date: `${value} 09:00:00` });
    }
  };

  const handleNext = () => {
  if (!consentBundleId) {
    toast.error("Missing consent bundle ID.");
    return;
  }
  if (!clinician) {
    toast.error("Please select a clinician before proceeding.");
    return;
  }

  // If empty note -> show modal (modal already supports "Proceed without letter" => onNext())
  if (!note || note.trim() === "") {
    setShowEmptyNoteModal(true);
    return;
  }

  // If edited -> ask confirm (confirm button already triggers setPractitioner.mutate)
  if (hasEditedNote) {
    setShowConfirmModal(true);
    return;
  }

  // ✅ Not edited but note exists => proceed with same generate flow
  setPractitioner.mutate({
    practitioner_id: clinician,
    load_clinician_templates: applyTemplate,
    should_generate_letter: false,
  });
};


const handleConfirmProceed = () => {
  setShowConfirmModal(false);
  setHasEditedNote(false);

  if (!note.trim()) {
    toast.info("No patient note provided — skipping letter generation.");
    onNext();
    return;
  }

  setPractitioner.mutate({
    practitioner_id: clinician,
    load_clinician_templates: applyTemplate,
    should_generate_letter: true,
  });
};

const handleCancelProceed = () => {
  setShowConfirmModal(false);
};


  const isBusy =
    isGenerating ||
    setPractitioner.isPending ||
    generatePatientLetter.isPending ||
    updateAppointment.isPending;

  return (
    <div className={styles.page}>


{/* Consent / Treatment Info */}
    <section className={styles.bundleInfoCard}>
      <div className={styles.bundleInfoRow}>

        {/* Consent Bundle ID */}
        <div className={styles.bundleItem}>
      <span className={styles.bundleLabel}>Consent bundle ID</span>
      <div className={styles.bundleValueRow}>
        <span className={styles.bundleValue}>
          {consentBundleId || "—"}
        </span>

        {consentBundleId && (
          <button
            type="button"
            className={styles.copyIconBtn}
            onClick={() => {
              navigator.clipboard.writeText(String(consentBundleId));
              toast.success("Consent bundle ID copied");
            }}
            aria-label="Copy consent bundle ID"
          >
            <ContentCopyOutlinedIcon fontSize="small" />
          </button>
        )}
      </div>
    </div>

    <div className={styles.bundleItem}>
      <span className={styles.bundleLabel}>Treatment plan ID</span>
      <div className={styles.bundleValueRow}>
        <span className={styles.bundleValue}>
          {treatmentData?.treatment_plan_id || "—"}
        </span>

        {treatmentData?.treatment_plan_id && (
          <button
            type="button"
            className={styles.copyIconBtn}
            onClick={() => {
              navigator.clipboard.writeText(
                String(treatmentData.treatment_plan_id)
              );
              toast.success("Treatment plan ID copied");
            }}
            aria-label="Copy treatment plan ID"
          >
            <ContentCopyOutlinedIcon fontSize="small" />
          </button>
        )}
      </div>
    </div>


        {/* Code */}
        <div className={styles.bundleItem}>
          <span className={styles.bundleLabel}>Code</span>
          <span className={styles.bundleValue}>
            {treatmentData?.code || "—"}
          </span>
        </div>

        {/* Version */}
        <div className={styles.bundleItem}>
          <span className={styles.bundleLabel}>Version</span>
          <span className={styles.bundleValue}>
            v{treatmentData?.version_no || "2.0.0"}
          </span>
        </div>

      </div>
    </section>



      {/* Clinician Selection */}
      <section className={styles.card}>
        <h3 className={styles.cardTitle}>Clinician selection</h3>
        <label className={styles.field}>
          <span className={styles.label}>Select clinician</span>
          <div className={styles.selectWrap}>
            <select
              className={styles.select}
              value={clinician}
              onChange={(e) => setClinician(e.target.value)}
              disabled={isBusy}
            >
              <option value="">Choose a clinician</option>
              {clinicianObj?.id && (
                <option value={clinicianObj.id}>
                  {clinicianObj.name} ({clinicianObj.role})
                </option>
              )}
            </select>
            <span className={styles.selectChevron} aria-hidden>
              ▾
            </span>
          </div>
          <small className={styles.helpText}>
            Templates will be loaded based on what the clinician has created for the practice.
          </small>
        </label>

        <label className={styles.checkboxRow}>
          <input
            type="checkbox"
            checked={applyTemplate}
            onChange={(e) => setApplyTemplate(e.target.checked)}
            disabled={isBusy}
          />
          <span>Apply this clinician’s consent template</span>
        </label>
      </section>

      {/* Patient Information */}
      <section className={styles.card}>
        <h3 className={styles.cardTitle}>Patient information</h3>
        <div className={styles.grid}>
          <label className={styles.field}>
            <span className={styles.label}>Name</span>
            <input
              className={styles.input}
              value={`${patient.title || ""} ${patient.first_name || ""} ${patient.last_name || ""}`}
              readOnly
            />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>Mobile</span>
            <input className={styles.input} value={patient.mobile_phone || "N/A"} readOnly />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>Email</span>
            <input className={styles.input} value={patient.email_address || "N/A"} readOnly />
          </label>
          <label className={styles.field} style={{ gridColumn: "1 / -1" }}>
            <span className={styles.label}>Address</span>
            <input
              className={styles.input}
              value={
                patient.address_line_1 ||
                patient.address_line_2 ||
                patient.town ||
                patient.postcode ||
                "No address provided"
              }
              readOnly
            />
          </label>
        </div>
      </section>

      {/* Due Section */}
      <section className={styles.card}>
        <h3 className={styles.cardTitle}>Due</h3>
        <label className={styles.field}>
          <span className={styles.label}>Schedule appointment date</span>
          <div className={styles.dueRow}>
            <select
              className={styles.select}
              value={dueOption}
              onChange={(e) => handleDueOptionChange(e.target.value)}
              disabled={isBusy}
            >
              <option value="1 day">1 Day</option>
              <option value="1 week">1 Week</option>
              <option value="2 weeks">2 Weeks</option>
              <option value="3 weeks">3 Weeks</option>
              <option value="1 month">1 Month</option>
            </select>
            <span className={styles.orText}>or</span>
            <input
              type="date"
              className={styles.input}
              value={customDate}
              onChange={(e) => handleCustomDateChange(e.target.value)}
              disabled={isBusy}
            />
          </div>
          <small className={styles.helpText}>
            Choose a specific date or let the system calculate automatically.
          </small>
        </label>
      </section>

      {/* AI Letter Generator */}
      <div className={styles.subBlock}>
        <h4 className={styles.subTitle}>AI patient letter generator</h4>
        <div className={styles.noticeBox}>
        <span className={styles.noticeIcon}>ℹ️</span>
          <span className={styles.noticeText}>
            No need to worry, all your data is GDPR compliant and fully encrypted
          </span>
        </div>

        <div className={styles.textareaWrapper}>
          <textarea  style={{ height: "300px" }}
            className={styles.textarea}
            rows={6}
            placeholder="(Add patient note here)
            MH: Prev ortho + veneers. Wants whiter, wider smile with lower tooth show. Prefers veneers.

            O/E: UL12 crowns, UR12 veneers, Class II div I, full overbite, missing UL3, TMJ click LHS. OH good, mild gingivitis.

            Dx/Advice: Local gingivitis – improve OH, 3–6 m recalls."
            value={note}    
            onChange={(e) => {
              setNote(e.target.value);
              setHasEditedNote(true);
            }}
            disabled={isBusy}
          />


         {isGenerating && (
          <div className={styles.generatingOverlay}>
            <div className={styles.generatingContainer}>
              <Lottie
                animationData={generatingAnim}
                loop
                autoplay
                style={{ width: 220, height: 220 }} 
              />
              <p className={styles.generatingText}>Generating letter...</p>
            </div>
          </div>
        )}

        </div>

        <div className={styles.badgesRow}>
          <span className={`${styles.badge} ${styles.badgeSoft}`}>
            Version: <strong>v{treatmentData?.version_no || "2.0.0"}</strong>
          </span>
          <span className={`${styles.badge} ${styles.badgeOutline}`}>
            Mode: <strong>{treatmentData?.mode || "Consent_Bundle"}</strong>
          </span>
        </div>
      </div>



{/* Treatment Plan Section */}
{allTreatments.length > 0 && (
  <div className={styles.subBlock}>
    
    <h4 className={styles.subTitle}>Treatment Plans</h4>


    <div className={styles.bubbleContainer}>
      {allTreatments.map((t) => (
        <div key={t.id} className={styles.treatmentBubble}>
          <span className={styles.bubbleTitle}>{t.nomenclature}</span>

          {t.tooth && t.tooth !== "{}" && (
            <span className={styles.bubbleTooth}>{t.tooth}</span>
          )}

          <span className={styles.bubblePlan}>
            Payment Plan: {t.payment_plan}
          </span>

          <span className={styles.bubblePrice}>£{t.price}</span>
        </div>
      ))}
    </div>

    <div className={styles.totalPriceUnderBubbles}>
      <span>Total: </span>
      <strong>£{totalPrice.toFixed(2)}</strong>
    </div>

  </div>
)}





      {/* Footer */}
      <div className={styles.footer}>
        <button className={flow.btnOutline} onClick={onBack} disabled={isBusy}>
          Back
        </button>

        <div style={{ display: "flex", gap: "10px" }}>
          {/* Generate + Next */}
          {/* <button className={flow.btnPrimary} onClick={handleNext} disabled={isBusy}>
            {setPractitioner.isPending
              ? "Saving clinician..."
              : isGenerating
              ? "Generating letter..."
              : "Generate & Next"}
          </button> */}




          <div style={{ display: "flex", gap: "10px" }}>
            <button
              className={flow.btnPrimary}
              onClick={handleNext}
              disabled={isBusy}
              style={{ background: "#e25a1bff" }}
            >
              {setPractitioner.isPending
                ? "Saving clinician..."
                : isGenerating
                ? "Generating letter..."
                : "Next"}
            </button>
          </div>

{/* 
          <button
            className={flow.btnPrimary}
            onClick={onNext}
            disabled={isBusy}
            style={{ background: "#e25a1bff" }} 
          >
            Next
          </button> */}
        </div>
      </div>



        {showConfirmModal && (
          <div className={styles.modalOverlay}>
            <div className={styles.modal}>
              <h4>Confirm change</h4>
              <p>
                  You edited the patient notes. Continuing will regenerate the consent letter and 
                  update its content based on your changes.  
                  <br /><br />
                  Would you like to create a new updated letter?
                </p>
              <div className={styles.modalActions}>
                <button onClick={handleCancelProceed} className={flow.btnOutline}>
                  Cancel
                </button>
                <button onClick={handleConfirmProceed} className={flow.btnPrimary}>
                  Yes, proceed
                </button>
              </div>
            </div>
          </div>
        )}

{showEmptyNoteModal && (
  <div className={styles.modalOverlay}>
    <div className={styles.modal}>
      <h4>No patient note</h4>

      <p>
        There is no information to generate a patient letter.
        <br />
        Add a note now or continue without generating a letter.
      </p>

      <button
        onClick={() => {
          setShowEmptyNoteModal(false);
          setShowExampleModal(true);
        }}
        className={styles.exampleBtn}
      >
        <InfoOutlinedIcon className={styles.infoPulseIcon} />
        <span>View example patient note</span>
      </button>

      {/* 🔵 reassurance message */}
      <div className={styles.reassureBox}>
  <InfoOutlinedIcon className={styles.reassureIcon} />
  <span>You can always come back and generate the patient letter later if needed.</span>
</div>

      <div className={styles.modalActions}>
        <button
          className={flow.btnOutline}
          onClick={() => {
            setShowEmptyNoteModal(false);
            const el = document.querySelector(`.${styles.textarea}`);
            if (el) el.focus();
          }}
        >
          Add note
        </button>

        <button
          className={flow.btnPrimary}
          onClick={() => {
            setShowEmptyNoteModal(false);
            onNext();
          }}
        >
          Proceed without letter
        </button>
      </div>
    </div>
  </div>
)}


{showExampleModal && (
  <div className={styles.modalOverlay}>
    <div
      className={styles.modal}
      style={{
        maxWidth: "650px",
        maxHeight: "75vh",
        overflowY: "auto",
        padding: "22px",
        borderRadius: "14px",
        background: "#fff",
        boxShadow: "0 8px 20px rgba(0,0,0,0.12)",
        textAlign: "left",
      }}
    >
      <h3
        style={{
          margin: "0 0 14px 0",
          fontWeight: 600,
          fontSize: "18px",
          textAlign: "left",
        }}
      >
        Example patient note
      </h3>

      <p
        style={{
          whiteSpace: "pre-line",
          lineHeight: "1.55",
          fontSize: "14px",
          color: "#444",
          background: "#f9fafb",
          padding: "16px",
          borderRadius: "10px",
          border: "1px solid #eee",
          textAlign: "left",
        }}
      >
<strong>MH:</strong> Checked PCO: had a lot of treatment in the past, such as ortho 
as a child, veneers as an adult. Wants a better smile as he hasn’t reached his 
ideal look. Lower teeth don’t show; prefers lower tooth display. Wants a wider 
and whiter smile. Avoids Invisalign (long treatment) and composite bonding (short 
lifespan). Interested in veneers. Aware LLQ bridge may be an issue.

{"\n\n"}<strong>SH:</strong> NAD

{"\n\n"}<strong>O/E:</strong> UL12 crowns, UR12 veneers, incisal wear, Class II div I, heavy 
overbite with no lower tooth display. Narrow smile, UL3 missing. TMJ click on LHS. 
Oral cancer screen: NAD. OH: Good. Mild gingivitis.

{"\n\n"}<strong>Diagnosis:</strong> Chronic localised gingivitis.

{"\n\n"}<strong>Advice:</strong> OHIG – improve brushing time + add floss/TePe brushes at night. 
Recommend 3–6 month cleans.

{"\n\n"}<strong>Patient has chosen:</strong> Veneers.
      </p>

      <div
        className={styles.modalActions}
        style={{ justifyContent: "flex-start", marginTop: "16px" }}
      >
        <button
          className={flow.btnPrimary}
          onClick={() => setShowExampleModal(false)}
        >
          Close
        </button>
      </div>
    </div>
  </div>
)}




    </div>
  );
}